/** Roles and permissions: the original ImageSync's Manage Users model, without the billing parts. */
export const ROLES = ['Admin', 'Cashier', 'Staff', 'Public'];
export const FLAG_KEYS = [
    'originals', 'allCaseTypes', 'restrictedBooks', 'grayscale', 'editRecords',
    'importDocs', 'deleteRecords', 'pdf', 'coverPage', 'email',
];
export const PERM_GROUPS = [
    { label: 'Viewing', items: [
            { key: 'originals', label: 'Originals and redaction', hint: 'See unredacted originals, draw redactions and save rotation' },
            { key: 'allCaseTypes', label: 'All case types', hint: 'Otherwise public case types only, so guardianship records stay hidden' },
            { key: 'restrictedBooks', label: 'Restricted books', hint: 'Open books marked restricted' },
            { key: 'grayscale', label: 'Open pages in grayscale', hint: '' },
        ] },
    { label: 'Editing', items: [
            { key: 'editRecords', label: 'Edit index records', hint: 'Correct estate record details' },
            { key: 'importDocs', label: 'Import documents', hint: 'Check and import scanned files' },
            { key: 'deleteRecords', label: 'Delete records', hint: 'Delete estate records (restorable) and remove documents from books' },
        ] },
    { label: 'Copies', items: [
            { key: 'printers', label: 'Counter printers', hint: 'Which printers this account can send jobs to' },
            { key: 'pdf', label: 'Downloads', hint: 'PDFs of pages and CSV exports of search results' },
            { key: 'coverPage', label: 'Cover page on copies', hint: '' },
            { key: 'email', label: 'Email documents', hint: 'Send documents by email' },
        ] },
];
export function emptyPerms() {
    const p = { printers: [] };
    for (const k of FLAG_KEYS)
        p[k] = false;
    p.coverPage = true;
    return p;
}
/** Coerce anything stored or submitted into a complete, clean permission set. */
export function normPerms(input) {
    const src = (input && typeof input === 'object' ? input : {});
    const out = emptyPerms();
    for (const k of FLAG_KEYS)
        out[k] = src[k] === true;
    const printers = Array.isArray(src.printers) ? src.printers.filter((x) => typeof x === 'string') : [];
    out.printers = Array.from(new Set(printers)).sort();
    return out;
}
/** Defaults for a new office: the original's tblDefaultPermissions rows. Admin saw originals and restricted books and
 *  could edit, import and delete; Staff and Cashiering saw restricted books but not originals, and could edit nothing;
 *  everyone but the public saw every case type. Copies: Admin and Cashiering downloaded PDFs, Staff printed locally
 *  (a PDF too, in effect), the public nothing until an administrator chose a method. Email was switched off. */
export function defaultRolePerms(role, printerIds) {
    const all = [...printerIds];
    const p = emptyPerms();
    switch (role) {
        case 'Admin':
            Object.assign(p, { originals: true, allCaseTypes: true, restrictedBooks: true, editRecords: true, importDocs: true, deleteRecords: true, pdf: true, coverPage: true, printers: all });
            break;
        case 'Staff':
            Object.assign(p, { allCaseTypes: true, restrictedBooks: true, pdf: true, coverPage: true, printers: all });
            break;
        case 'Cashier':
            Object.assign(p, { allCaseTypes: true, restrictedBooks: true, pdf: true, coverPage: true, printers: all });
            break;
        case 'Public':
            Object.assign(p, { coverPage: true });
            break;
    }
    return normPerms(p);
}
export function isRestrictedCaseType(s, caseType) {
    const t = caseType.trim().toLowerCase();
    if (s.publicCaseTypes)
        return !s.publicCaseTypes.some(v => v.trim().toLowerCase() === t);
    return (s.restrictedCaseTypes ?? ['Guardianship']).some(v => v.trim().toLowerCase() === t);
}
/** Anyone at the counter uses the guest terminal, so it never gets what the public mustn't have. */
export const GUEST_BLOCKED = ['originals', 'allCaseTypes', 'restrictedBooks', 'editRecords', 'importDocs', 'deleteRecords'];
/** The guest terminal at the counter: public, no downloads, copies on the office's first printer. */
export function kioskPerms(printerIds) {
    return normPerms({ ...defaultRolePerms('Public', printerIds), pdf: false, printers: printerIds.length ? [printerIds[0]] : [] });
}
export const managesUsers = (role) => role === 'Admin' || role === 'Cashier';
/** Admins manage everyone in their office; cashiers manage registered public accounts only. */
export function canManageAccount(actorRole, target) {
    if (actorRole === 'Admin')
        return true;
    if (actorRole === 'Cashier')
        return target.role === 'Public' && target.kind === 'user';
    return false;
}
export function permChanges(before, after) {
    const out = [];
    const label = (k) => PERM_GROUPS.flatMap(g => g.items).find(i => i.key === k)?.label ?? k;
    for (const k of FLAG_KEYS)
        if (before[k] !== after[k])
            out.push((after[k] ? 'allowed ' : 'removed ') + label(k).toLowerCase());
    const added = after.printers.filter(p => !before.printers.includes(p));
    const removed = before.printers.filter(p => !after.printers.includes(p));
    if (added.length)
        out.push('printers added: ' + added.join(', '));
    if (removed.length)
        out.push('printers removed: ' + removed.join(', '));
    return out;
}
//# sourceMappingURL=permissions.js.map